import { ReactComponent as Rink } from "./assets/icehockey-court.svg";
import * as service from "../../service";
import React, {
  FC,
  memo,
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import { Overlay, TimerController } from "../../components";
import {
  MESSAGES,
  TIMER_CONTINUE_EVENTS,
  TIMER_PAUSE_EVENTS,
  ICONS,
} from "./constants";
import { LANGUAGE } from "_constants";
import { Carousel } from "react-responsive-carousel";
import AdDisplay from "components/common/AdDisplay";
import AdBannerSvg from "./assets/ad-banner.svg";
import GeniusFeeds from "../../assets/ads/genius-logo.svg";
import FastGamesApi from "../../assets/ads/fastgamesapi.svg";
import Monetria from "../../assets/ads/monetria.svg";
import { IceHockeyStatusRenderer } from "./components/StatusRenderer";
import { LivePuck } from "./components/Puck";
import { Timer } from "./components/Timer";
import PreMatchIntro from "../../components/PreMatchIntro";
import { ICE_TABS, IceTabKey, processStatus } from "./utils";
import { AttackIndicator } from "./indicators/AttackIndicator";
import { PenaltyIndicator } from "./indicators/PenaltyIndicator";
import { PowerPlayIndicator } from "./indicators/PowerPlay";
import IceHockeyMatchTimeline, {
  buildIceHockeyMatchTimelineEvents,
} from "./timeline";
import { IcingIndicator } from "./indicators/IcingIndicator";
import { SportTabs } from "components/common/SportTabs";
import { Stats } from "./statistics";
import IceHockeyArena from "./assets/icehockey-bg.png";
import {
  AdBanner,
  FlatLayer,
  FullHeightTabPanel,
  createBackgroundComponent,
} from "../../components/common";

export type IceHockeyProps = {
  status: service.MatchStatus;
  language?: LANGUAGE;
};

const PRE_MATCH_STATUS_CODES = new Set(["1200", "01200"]);

const StyledCourtIceHockey = createBackgroundComponent({
  backgroundImage: IceHockeyArena,
  baseClassName:
    "relative flex h-full w-full items-center justify-center overflow-hidden bg-cover bg-center",
  displayName: "StyledCourtIceHockey",
  useImageElement: true,
});

export const IceHockey: FC<IceHockeyProps> = memo(
  ({ status, language = LANGUAGE.ENGLISH }) => {
    const [activeTab, setActiveTab] = useState<IceTabKey>("narration");
    const [displayedStatus, setDisplayedStatus] = useState(status);
    const animatingRef = useRef(false);
    const pendingRef = useRef<service.MatchStatus | null>(null);

    useEffect(() => {
      if (animatingRef.current) {
        pendingRef.current = status;
      } else {
        setDisplayedStatus(status);
      }
    }, [status]);

    const { normalizedStatusCode } = useMemo(
      () => processStatus(displayedStatus, language),
      [displayedStatus, language]
    );

    const matchEvents = useMemo(
      () => buildIceHockeyMatchTimelineEvents(status),
      [status]
    );

    const hasMatchTimeline = useMemo(
      () => matchEvents.length > 0,
      [matchEvents]
    );

    const tabs = useMemo(
      () =>
        ICE_TABS.filter((tab) => tab.key !== "timeline" || hasMatchTimeline),
      [hasMatchTimeline]
    );

    useEffect(() => {
      if (!tabs.some((tab) => tab.key === activeTab) && tabs.length > 0) {
        setActiveTab(tabs[0].key);
      }
    }, [tabs, activeTab]);

    const selectedTabIndex = useMemo(() => {
      const idx = tabs.findIndex((tab) => tab.key === activeTab);
      return idx >= 0 ? idx : 0;
    }, [tabs, activeTab]);

    const messageGenerator = useCallback(
      ({
        language,
        normalizedStatusCode,
        statusMessage,
      }: {
        language: LANGUAGE;
        normalizedStatusCode?: string;
        statusMessage?: string;
      }) => {
        if (!normalizedStatusCode) return undefined;
        const langDict = MESSAGES[language];
        if (!langDict) return undefined;

        let message =
          normalizedStatusCode === "1280"
            ? statusMessage ?? ""
            : langDict[normalizedStatusCode] ?? "";

        const Icon = ICONS[normalizedStatusCode];

        return { message: message?.toUpperCase(), icon: Icon };
      },
      []
    );

    const isPreMatch = useMemo(() => {
      const scRaw =
        typeof displayedStatus?.sc === "string"
          ? displayedStatus.sc
          : typeof displayedStatus?.sc === "number"
          ? String(displayedStatus.sc)
          : "";
      const normalizedCode =
        displayedStatus?.nsc !== undefined && displayedStatus?.nsc !== null
          ? String(displayedStatus.nsc)
          : "";

      const hasPreMatchCode =
        PRE_MATCH_STATUS_CODES.has(normalizedCode) ||
        PRE_MATCH_STATUS_CODES.has(scRaw);
      if (hasPreMatchCode) return true;

      const stsVal = displayedStatus?.sts as unknown;
      const stsNum =
        typeof stsVal === "number"
          ? stsVal
          : typeof stsVal === "string"
          ? Number(stsVal)
          : NaN;
      const notStartedByStatus = !Number.isNaN(stsNum) && stsNum === 0;

      const tmVal = displayedStatus?.tm as unknown;
      const tmNum =
        typeof tmVal === "number"
          ? tmVal
          : typeof tmVal === "string"
          ? Number(tmVal)
          : NaN;
      const notStartedByTime = !Number.isNaN(tmNum) && tmNum <= 0;

      const noLiveNsc = !normalizedCode;
      return (notStartedByStatus || notStartedByTime) && noLiveNsc;
    }, [displayedStatus]);

    return (
      <>
        <SportTabs
          tabs={tabs}
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as IceTabKey)}
        />

        <Carousel
          showThumbs={false}
          selectedItem={selectedTabIndex}
          autoPlay={false}
          emulateTouch
          showStatus={false}
          showArrows={false}
          showIndicators={false}
          swipeable={false}
        >
          {tabs.map((tab) => {
            if (tab.key === "narration")
              return (
                <StyledCourtIceHockey className="styled-court" key="narration">
                  <Timer status={displayedStatus} />
                  <AdBanner src={AdBannerSvg} alt="Ad Banner" />
                  <AdDisplay adImages={[GeniusFeeds, FastGamesApi, Monetria]} />

                  <Overlay
                    status={displayedStatus}
                    messageGenerator={messageGenerator}
                    language={language}
                    StatusRenderer={IceHockeyStatusRenderer}
                  >
                    <Rink />
                    {!isPreMatch && (
                      <>
                        <LivePuck status={displayedStatus} />
                        <AttackIndicator status={displayedStatus} />
                        <PenaltyIndicator status={displayedStatus} />
                        <PowerPlayIndicator status={displayedStatus} />
                        <IcingIndicator status={displayedStatus} />
                      </>
                    )}
                  </Overlay>

                  {isPreMatch && (
                    <FlatLayer>
                      <PreMatchIntro status={displayedStatus} />
                    </FlatLayer>
                  )}
                </StyledCourtIceHockey>
              );

            if (tab.key === "stats")
              return (
                <FullHeightTabPanel key="stats">
                  <Stats status={status} />
                </FullHeightTabPanel>
              );

            if (tab.key === "timeline")
              return (
                <FullHeightTabPanel key="timeline">
                  <IceHockeyMatchTimeline
                    events={matchEvents}
                    status={status}
                  />
                </FullHeightTabPanel>
              );

            return null;
          })}
        </Carousel>

        <TimerController
          event={normalizedStatusCode}
          eventsContinue={TIMER_CONTINUE_EVENTS}
          eventsPause={TIMER_PAUSE_EVENTS}
        />
      </>
    );
  }
);

export { StyledCourtIceHockey as StyledCourtIce, Overlay };
